import React, { useEffect, useState } from 'react'
import logo from '../assets/img/full_logo_color_white.svg'
import {
  getAllProjects,
  getCategories,
  prepareProjectsForDisplay
} from '../utils/portfolioUtils'
import './PurechainMockup.css'

/*
THESIS: A private network made visible; reject the generic crypto landing page and public-fund funnel.
OWN-WORLD: Carbon and parchment surfaces, oxidised-red emphasis, hairline geometry, flat square controls.
STORY: Serious founders and aligned private investors recognise fit, examine the portfolio, then request an introduction.
FIRST VIEWPORT: Quiet navigation, a three-line selective headline, two qualified contact paths, and the relationship orbit.
FORM: Private-office network, chosen from the two mockups; hero and portfolio share one restrained visual grammar.
*/

const founderEmail = 'mailto:team@purechaincapital.com?subject=Founder%20introduction'
const partnerEmail = 'mailto:team@purechaincapital.com?subject=Private%20partner%20enquiry'

const navigation = [
  { href: '#approach', label: 'Approach' },
  { href: '#portfolio', label: 'Portfolio' },
  { href: '#contact', label: 'Contact' }
]

function Header ({ label }) {
  return (
    <header className='pc-header'>
      <a className='pc-logo' href='#top' aria-label='Purechain Capital home'>
        <img src={logo} alt='Purechain Capital' />
      </a>
      <nav aria-label='Primary navigation'>
        {navigation.map(item => <a href={item.href} key={item.href}>{item.label}</a>)}
      </nav>
      <span className='pc-concept-label'>{label}</span>
      <details className='pc-mobile-nav'>
        <summary>Menu</summary>
        <nav aria-label='Mobile navigation'>
          {navigation.map(item => (
            <a
              href={item.href}
              key={item.href}
              onClick={event => event.currentTarget.closest('details')?.removeAttribute('open')}
            >
              {item.label}
            </a>
          ))}
        </nav>
      </details>
    </header>
  )
}

function Portfolio ({ projects, layout, status }) {
  const [category, setCategory] = useState('all')
  const categories = getCategories()
  const visibleProjects = category === 'all'
    ? projects
    : projects.filter(project => project.category === category)

  return (
    <section className='pc-portfolio pc-section' id='portfolio' aria-labelledby='portfolio-heading'>
      <div className='pc-section-heading'>
        <p className='pc-eyebrow'>Selected portfolio</p>
        <h2 id='portfolio-heading'>Conviction, made visible.</h2>
        <p>A selective portfolio across AI, DeFi, gaming and infrastructure.</p>
      </div>
      <div className='pc-filters' aria-label='Filter portfolio by sector'>
        {categories.map(item => (
          <button
            aria-label={item.name}
            aria-pressed={category === item.id}
            className={category === item.id ? 'is-active' : ''}
            disabled={status !== 'ready'}
            key={item.id}
            onClick={() => setCategory(item.id)}
            type='button'
          >
            {item.id === 'all' ? 'All' : item.id}
          </button>
        ))}
      </div>
      <p className='pc-portfolio-count' aria-live='polite'>
        {status === 'ready' && `${visibleProjects.length} ${visibleProjects.length === 1 ? 'company' : 'companies'}`}
      </p>
      {status === 'loading' && (
        <div className={`pc-projects pc-projects-${layout} pc-projects-loading`} aria-label='Loading portfolio'>
          {[0, 1, 2, 3, 4, 5].map(item => <span className='pc-project-skeleton' key={item} />)}
        </div>
      )}
      {status === 'error' && (
        <div className='pc-portfolio-status' role='alert'>
          <strong>Portfolio unavailable.</strong>
          <span>Please refresh the page or contact the team directly.</span>
        </div>
      )}
      <div
        aria-busy={status === 'loading'}
        className={`pc-projects pc-projects-${layout}`}
      >
        {visibleProjects.map((project, index) => (
          <a
            aria-label={`${project.name} website — opens in a new tab`}
            className='pc-project'
            href={project.socialLinks.website}
            key={project.id}
            rel='noreferrer'
            target='_blank'
          >
            <span className='pc-project-number'>{String(index + 1).padStart(2, '0')}</span>
            <span className='pc-project-logo' data-logo={project.id}>
              <img src={project.icon} alt='' decoding='async' height='80' loading='lazy' width='160' />
            </span>
            <span className='pc-project-copy'>
              <strong>{project.name}</strong>
              <span>{project.description}</span>
            </span>
            <span className='pc-project-meta'>{project.category}</span>
            <span className='pc-project-arrow' aria-hidden='true'>↗</span>
          </a>
        ))}
      </div>
      {status === 'ready' && visibleProjects.length === 0 && (
        <p className='pc-portfolio-status'>No companies are listed in this category yet.</p>
      )}
    </section>
  )
}

function Contact ({ privateFirst = false }) {
  const paths = privateFirst
    ? [
        {
          label: 'For prospective partners',
          title: 'Begin a private conversation.',
          copy: 'We form a small number of aligned relationships. Introductions are considered privately and individually.',
          href: partnerEmail,
          action: 'Private partner enquiry'
        },
        {
          label: 'For founders',
          title: 'Building with real conviction?',
          copy: 'We consider focused introductions from exceptional teams building consequential crypto infrastructure and applications.',
          href: founderEmail,
          action: 'Introduce your project'
        }
      ]
    : [
        {
          label: 'For founders',
          title: 'Show us what others are missing.',
          copy: 'We consider focused introductions from exceptional teams building consequential crypto products.',
          href: founderEmail,
          action: 'Introduce your project'
        },
        {
          label: 'For prospective partners',
          title: 'A deliberately small circle.',
          copy: 'We form select private relationships with experienced investors who share our long-term view.',
          href: partnerEmail,
          action: 'Request a private conversation'
        }
      ]

  return (
    <section className='pc-contact pc-section' id='contact'>
      {paths.map(path => (
        <article className='pc-contact-card' key={path.label}>
          <p className='pc-eyebrow'>{path.label}</p>
          <h2>{path.title}</h2>
          <p>{path.copy}</p>
          <a className='pc-text-link' href={path.href}>{path.action} <span aria-hidden='true'>↗</span></a>
        </article>
      ))}
    </section>
  )
}

function Footer () {
  return (
    <footer className='pc-footer'>
      <img src={logo} alt='Purechain Capital' />
      <p>Selective capital for the next crypto economy.</p>
      <p>© 2026 Purechain Capital. All rights reserved.</p>
    </footer>
  )
}

function MemoConcept ({ projects, status }) {
  return (
    <main className='pc-site pc-memo' id='top'>
      <Header label='Concept 01 — Conviction Memo' />
      <section className='pc-memo-hero'>
        <div className='pc-memo-title'>
          <p className='pc-eyebrow'>Purechain Capital / 2026</p>
          <h1>We back the builders moving crypto <em>forward.</em></h1>
        </div>
        <div className='pc-memo-intro'>
          <p>Early conviction for founders building enduring infrastructure, financial systems, and digital experiences.</p>
          <a className='pc-primary-action' href={founderEmail}>Introduce your project <span aria-hidden='true'>↗</span></a>
        </div>
        <div className='pc-memo-note'>
          <span>01</span>
          <p>Independent thinking.<br />Patient relationships.<br />Crypto-native perspective.</p>
        </div>
      </section>
      <section className='pc-memo-thesis pc-section' id='approach'>
        <p className='pc-eyebrow'>Our approach</p>
        <p className='pc-statement'>Capital matters. Clear thinking, aligned incentives, and the right introductions compound it.</p>
        <div className='pc-principles'>
          <div><span>01</span><strong>Founder-led</strong><p>We look for unusually clear teams with the ambition to define a category.</p></div>
          <div><span>02</span><strong>Early conviction</strong><p>We engage where informed belief and practical support can matter most.</p></div>
          <div><span>03</span><strong>Long horizon</strong><p>We value durable networks and useful products over short-lived narratives.</p></div>
        </div>
      </section>
      <Portfolio projects={projects} layout='list' status={status} />
      <Contact />
      <Footer />
    </main>
  )
}

function PrivateConcept ({ projects, status }) {
  return (
    <main className='pc-site pc-private' id='top'>
      <Header label='Private by design' />
      <section className='pc-private-hero' aria-labelledby='private-heading'>
        <div className='pc-private-kicker'>Private capital / Crypto conviction / Selective by design</div>
        <div className='pc-private-title'>
          <h1 aria-label='Built on conviction. Relationships begin by introduction.' id='private-heading'>
            Built on conviction.<br /><span aria-hidden='true'>Relationships begin<br />by introduction.</span>
          </h1>
          <p>Purechain Capital backs exceptional crypto founders and forms select, long-term relationships with aligned private investors.</p>
        </div>
        <div className='pc-private-actions'>
          <a className='pc-primary-action' href={partnerEmail}>Private partner enquiry <span aria-hidden='true'>↗</span></a>
          <a className='pc-secondary-action' href={founderEmail}>Founder introduction</a>
        </div>
        <div className='pc-orbit' aria-hidden='true'>
          <span className='pc-orbit-mark'>PCC</span>
          <span>FOUNDER</span><span>CONVICTION</span><span>PARTNER</span><span>CAPITAL</span>
        </div>
      </section>
      <section className='pc-private-approach pc-section' id='approach' aria-labelledby='approach-heading'>
        <div>
          <p className='pc-eyebrow'>A considered partnership</p>
          <h2 id='approach-heading'>Selective by design.<br />Long-term by nature.</h2>
        </div>
        <div className='pc-private-copy'>
          <p>We work closely with a concentrated set of founders and partners. Every relationship starts with shared conviction, earned trust, and direct conversation.</p>
          <p className='pc-disclaimer'>Partnership discussions are private and considered individually. An enquiry does not constitute an offer or guarantee participation.</p>
        </div>
      </section>
      <Portfolio projects={projects} layout='grid' status={status} />
      <Contact privateFirst />
      <Footer />
    </main>
  )
}

export default function PurechainMockup ({ concept }) {
  const [projects, setProjects] = useState([])
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    let active = true
    prepareProjectsForDisplay(getAllProjects()).then(result => {
      if (active) {
        setProjects(result)
        setStatus('ready')
      }
    }).catch(() => {
      if (active) setStatus('error')
    })
    return () => { active = false }
  }, [])

  return concept === 'private'
    ? <PrivateConcept projects={projects} status={status} />
    : <MemoConcept projects={projects} status={status} />
}
